import { useLazyQuery, useQuery } from "@apollo/client";
import { ReactElement, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import Select from "react-select";

import ErrorMessage from "../ui/Alerts/Error";
import Loading from "../ui/Alerts/Loading";
import {
  HorizontalNavigation,
  HorizontalNavigationOption,
} from "../ui/navigation/HorizontalNavigation";

import {
  AddressField,
  DateField,
  EmailField,
  EmploymentStatusField,
  EmploymentTypeField,
  FirstNameField,
  GenderField,
  JobField,
  LastNameField,
  ManagerField,
  PersonnalNumberField,
  PhotoField,
  SalaryField,
  UnitField,
} from "../common/form";

import { JOB_BY_ID, JOB_MANY } from "../../schema/job";

import {
  Address,
  Competence,
  Employee,
  EmploymentStatus,
  EmploymentType,
  Job,
  Revenue,
} from "../../types";

import { Divider } from "../ui/layout/Divider";

const tabs: HorizontalNavigationOption[] = [
  { value: "employee", label: "Mitarbeiter" },
  { value: "org", label: "Unternehmen" },
  { value: "address", label: "Anschrift" },
];

interface Props {
  onSubmit: (formData: any) => void;
  data?: Employee;
}
export interface FormData {
  firstName: string;
  lastName: string;
  gender: string;
  email: string;
  phone: string;
  address: Address;
  photo: string;
  birthDate: Date;
  hireDate: Date;
  retireDate: Date;
  terminationDate: Date;
  personnalNumber: string;
  jobId: string;
  jobIds: Array<string>;
  salary: number;
  competences: Array<Competence>;
  unitId: string;
  managerId: string;
  employmentType: EmploymentType;
  employmentStatus: EmploymentStatus;
  revenues: Array<Revenue>;
  fileIds: Array<string>;
}

const Form = ({ onSubmit, data }: Props) => {
  const methods = useForm<FormData>({ defaultValues: data });
  const { handleSubmit, control, watch, getValues, setValue } = methods;

  const { loading: jobsLoading, error: jobsError, data: jobsData } = useQuery(
    JOB_MANY
  );

  const selectedJobId = watch("jobId");
  const [getJob, { called: jobCalled, data: jobData }] = useLazyQuery(
    JOB_BY_ID,
    {
      variables: {
        _id: selectedJobId,
      },
    }
  );

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const jobOptions =
    jobsData?.jobMany.map(
      (job: Job) =>
        ({
          id: job._id,
          name: job.name,
        } as any)
    ) || [];

  let tab: ReactElement;
  switch (selectedTab.value) {
    case "address":
      tab = <AddressField />;
      break;
    case "org":
      tab = (
        <>
          <div className="space-y-1">
            <JobField />
          </div>
          <div className="space-y-1">
            {jobsLoading && <Loading />}
            {jobsError && <ErrorMessage error={jobsError} />}
            <label
              htmlFor="participantIds"
              className="block text-sm font-medium leading-5 text-gray-900"
            >
              Weitere Arbeitsplätze
            </label>
            <div className="mt-1">
              <Controller
                control={control}
                name="jobIds"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    placeholder="Arbeitsplätze wählen..."
                    options={jobOptions}
                    value={jobOptions.filter(({ id }: any) =>
                      value?.includes(id)
                    )}
                    getOptionLabel={({ name }: any) => name}
                    getOptionValue={({ id }: any) => id}
                    isLoading={jobsLoading}
                    isMulti
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onBlur={onBlur}
                    onChange={(values: any) =>
                      onChange((values ?? []).map((d: any) => d.id))
                    }
                  />
                )}
              />
            </div>
          </div>
          <div className="space-y-1">
            <UnitField />
          </div>
          <div className="py-1">
            <Divider />
          </div>
          <div className="flex space-x-6">
            <div className="w-1/2 space-y-1">
              <ManagerField />
            </div>
            <div className="w-1/2 space-y-1">
              <PersonnalNumberField />
            </div>
          </div>
          <div className="flex space-x-6">
            <div className="w-1/2 space-y-1">
              <DateField name="hireDate" label="Einstellungsdatum" />
            </div>
            <div className="w-1/2 space-y-1">
              <SalaryField />
            </div>
          </div>
          <div className="flex space-x-6">
            <div className="w-1/2 space-y-1">
              <EmploymentTypeField />
            </div>
            <div className="w-1/2 space-y-1">
              <EmploymentStatusField />
            </div>
          </div>
        </>
      );
      break;
    case "employee":
      tab = (
        <>
          <div className="flex space-x-6">
            <PhotoField />
          </div>
          <div className="flex space-x-6">
            <div className="space-y-1">
              <FirstNameField />
            </div>
            <div className="space-y-1">
              <LastNameField />
            </div>
          </div>
          <div className="flex space-x-6">
            <div className="w-1/2 space-y-1">
              <GenderField />
            </div>
            <div className="w-1/2 space-y-1">
              <EmailField />
            </div>
          </div>
          <div className="py-1">
            <Divider />
          </div>
          <div className="space-y-1">
            <DateField name="birthDate" label="Geburtsdatum" />
          </div>
          <div className="flex space-x-6">
            <div className="space-y-1">
              <DateField name="terminationDate" label="Vertragsendedatum" />
            </div>
            <div className="space-y-1">
              <DateField name="retireDate" label="Rentendatum" />
            </div>
          </div>
        </>
      );
      break;
    default:
      throw Error("Tab unreachable");
  }

  useEffect(() => {
    // for new employee records only
    if (!data?._id && selectedJobId) {
      //console.log("JobId changed to", selectedJobId);
      getJob({ variables: { _id: selectedJobId } });
    }
  }, [data, selectedJobId]);

  // useEffect(() => {
  //   // job data fetched
  //   if (jobData?.jobById) {
  //     // filter out unwanted attributes
  //     const competences = jobData.jobById?.competences.map((c: Competence) => ({
  //       skillId: c.skillId,
  //       value: c.value,
  //     }));
  //     // assign competences from job to employee
  //     setValue("competences", competences, {
  //       shouldValidate: false,
  //     });
  //   }
  // }, [jobCalled, jobData]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} id="slide-over-form">
          <div className="flex-1 flex flex-col justify-between">
            <div className="px-4 divide-y divide-gray-200 sm:px-6">
              <div className="space-y-6 pt-6 pb-5">
                <HorizontalNavigation
                  options={tabs}
                  selected={selectedTab}
                  onSelect={setSelectedTab}
                />
                {tab}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default Form;
